import { Plus, RotateCcw, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import {
    getShiftTypes,
    getDeletedShiftTypes,
    createShiftType,
    updateShiftType,
    deleteShiftType,
    restoreShiftType,
} from '../../../api/shift.api';
import styles from '../../../assets/scss/pages/PointRuleList.module.scss';
import { PERMISSIONS } from '../../../config/permissions';
import { usePermissions } from '../../../hooks/usePermissions';

function fmtTime(t) {
    if (!t) return '--:--';
    return t.slice(0, 5);
}

export default function ShiftList() {
    const { hasPermission } = usePermissions();
    const canCreate = hasPermission(PERMISSIONS.SHIFT_WORK.CREATE);
    const canUpdate = hasPermission(PERMISSIONS.SHIFT_WORK.UPDATE);
    const canDelete = hasPermission(PERMISSIONS.SHIFT_WORK.DELETE);

    const [shifts, setShifts] = useState([]);
    const [deletedShifts, setDeletedShifts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDeleted, setShowDeleted] = useState(false);
    const [toastMsg, setToastMsg] = useState(null);

    // Panel
    const [showPanel, setShowPanel] = useState(false);
    const [panelMode, setPanelMode] = useState('create');
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState({ name: '', startTime: '08:00', endTime: '17:00' });
    const [formError, setFormError] = useState('');
    const [saving, setSaving] = useState(false);

    const showToast = useCallback((type, text, duration = 1500) => {
        setToastMsg({ type, text });
        setTimeout(() => setToastMsg(null), duration);
    }, []);

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const [activeRes, deletedRes] = await Promise.all([
                getShiftTypes(),
                getDeletedShiftTypes(),
            ]);
            setShifts(activeRes.data.shiftTypes || []);
            setDeletedShifts(deletedRes.data.shiftTypes || []);
        } catch {
            setShifts([]);
            setDeletedShifts([]);
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchList(); }, [fetchList]);

    const openCreate = () => {
        setPanelMode('create');
        setEditId(null);
        setForm({ name: '', startTime: '08:00', endTime: '17:00' });
        setFormError('');
        setShowPanel(true);
    };

    const openEdit = (shift) => {
        if (!canUpdate) return;
        setPanelMode('edit');
        setEditId(shift.id);
        setForm({
            name: shift.name || '',
            startTime: fmtTime(shift.startTime),
            endTime: fmtTime(shift.endTime),
        });
        setFormError('');
        setShowPanel(true);
    };

    const closePanel = () => { setShowPanel(false); setFormError(''); setEditId(null); };

    const handleSave = async () => {
        if (!form.name.trim()) { setFormError('Vui lòng nhập tên ca'); return; }
        if (!form.startTime || !form.endTime) { setFormError('Vui lòng chọn giờ bắt đầu và kết thúc'); return; }
        if (form.startTime >= form.endTime) { setFormError('Giờ kết thúc phải sau giờ bắt đầu'); return; }

        setSaving(true);
        setFormError('');
        try {
            const payload = { name: form.name.trim(), startTime: form.startTime + ':00', endTime: form.endTime + ':00' };
            if (panelMode === 'create') {
                await createShiftType(payload);
                showToast('success', 'Tạo ca làm việc thành công');
            } else {
                await updateShiftType(editId, payload);
                showToast('success', 'Cập nhật thành công');
            }
            closePanel();
            fetchList();
        } catch (err) {
            setFormError(err.response?.data?.message || 'Thao tác thất bại');
        } finally { setSaving(false); }
    };

    const handleDelete = async () => {
        if (!canDelete || !editId) return;
        if (!window.confirm('Bạn có chắc muốn xoá ca làm việc này?')) return;
        try {
            await deleteShiftType(editId);
            showToast('success', 'Xoá thành công');
            closePanel();
            fetchList();
        } catch { showToast('error', 'Xoá thất bại', 2500); }
    };

    const handleRestore = async (e, shiftId) => {
        e.stopPropagation();
        if (!window.confirm('Khôi phục ca làm việc này?')) return;
        try {
            await restoreShiftType(shiftId);
            showToast('success', 'Khôi phục thành công');
            fetchList();
            setShowDeleted(false);
        } catch { showToast('error', 'Khôi phục thất bại', 2500); }
    };

    const currentList = showDeleted ? deletedShifts : shifts;

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h1 className={styles.title}>Ca làm việc</h1>
                    <p className={styles.subtitle}>
                        {showDeleted ? `${deletedShifts.length} ca đã xoá` : `${shifts.length} ca làm việc`}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                    {canDelete && (
                        <button className={styles.btn} style={{
                            background: '#fff', color: showDeleted ? '#0098F6' : '#6B7280',
                            border: '1.5px solid #ECEEF2',
                        }} onClick={() => { setShowDeleted(!showDeleted); closePanel(); }}>
                            <RotateCcw size={16} />
                            {showDeleted ? 'Quay lại' : 'Đã xoá'}
                        </button>
                    )}
                    {canCreate && !showDeleted && (
                        <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={openCreate}>
                            <Plus /> Tạo ca mới
                        </button>
                    )}
                </div>
            </div>

            <div className={styles.layout}>
                {/* LEFT: Grid */}
                <div className={styles.layoutLeft}>
                    {loading ? (
                        <div className={styles.loading}><div className={styles.spinner} /> Đang tải...</div>
                    ) : currentList.length === 0 ? (
                        <div className={styles.empty}>{showDeleted ? 'Không có ca đã xoá' : 'Chưa có ca làm việc nào'}</div>
                    ) : (
                        <div className={styles.grid}>
                            {currentList.map((s) => (
                                <div key={s.id}
                                    className={`${styles.card}${editId === s.id && !showDeleted ? ` ${styles.cardActive}` : ''}`}
                                    onClick={() => !showDeleted && openEdit(s)}
                                    style={showDeleted ? { cursor: 'default' } : undefined}
                                >
                                    <div>
                                        <div className={styles.cardFormula} style={{ marginBottom: 4 }}>{s.name}</div>
                                        <div className={styles.cardMeta}>{fmtTime(s.startTime)} - {fmtTime(s.endTime)}</div>
                                        {showDeleted && s.deletedAt && (
                                            <div style={{ fontSize: 12, color: '#EF4444', marginTop: 6 }}>
                                                Xoá lúc: {new Date(s.deletedAt).toLocaleString('vi-VN')}
                                            </div>
                                        )}
                                    </div>
                                    {showDeleted && canDelete && (
                                        <button style={{
                                            padding: '6px 14px', border: '1.5px solid #ECEEF2', borderRadius: 8,
                                            background: '#fff', fontSize: 13, fontWeight: 600, color: '#0098F6',
                                            cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
                                        }} onClick={(e) => handleRestore(e, s.id)}>
                                            Khôi phục
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* RIGHT: Panel */}
                {showPanel && !showDeleted && (
                    <div className={styles.layoutRight}>
                        <div className={styles.panel}>
                            <div className={styles.panelHeader}>
                                <h3 className={styles.panelTitle}>
                                    {panelMode === 'create' ? 'Tạo ca mới' : 'Chỉnh sửa'}
                                </h3>
                                <button className={styles.panelClose} onClick={closePanel}><X size={16} /></button>
                            </div>
                            <div className={styles.panelBody}>
                                {formError && <div className={styles.formError}>{formError}</div>}

                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Tên ca <span>*</span></label>
                                    <input className={styles.formInput} value={form.name}
                                        onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                                        placeholder="VD: Ca sáng, Ca chiều..." />
                                </div>

                                <div style={{ display: 'flex', gap: 12 }}>
                                    <div className={styles.formGroup} style={{ flex: 1 }}>
                                        <label className={styles.formLabel}>Bắt đầu <span>*</span></label>
                                        <input className={styles.formInput} type="time" value={form.startTime}
                                            onChange={(e) => setForm((p) => ({ ...p, startTime: e.target.value }))} />
                                    </div>
                                    <div className={styles.formGroup} style={{ flex: 1 }}>
                                        <label className={styles.formLabel}>Kết thúc <span>*</span></label>
                                        <input className={styles.formInput} type="time" value={form.endTime}
                                            onChange={(e) => setForm((p) => ({ ...p, endTime: e.target.value }))} />
                                    </div>
                                </div>
                            </div>

                            <div className={styles.panelFooter}>
                                {panelMode === 'edit' && canDelete && (
                                    <button className={`${styles.panelBtn} ${styles.panelBtnDanger}`} onClick={handleDelete}>Xoá</button>
                                )}
                                <div style={{ flex: 1 }} />
                                <button className={styles.panelBtn} onClick={closePanel}>Đóng</button>
                                <button className={`${styles.panelBtn} ${styles.panelBtnPrimary}`} onClick={handleSave} disabled={saving}>
                                    {saving ? 'Đang lưu...' : panelMode === 'create' ? 'Tạo' : 'Lưu'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {toastMsg && (
                <div className={styles.toastOverlay}>
                    <div className={styles.toastModal}>
                        <div className={styles.toastIcon} style={toastMsg.type === 'error' ? { background: '#EF4444' } : undefined}>
                            {toastMsg.type === 'success' ? '✓' : '✕'}
                        </div>
                        <span className={styles.toastText}>{toastMsg.text}</span>
                    </div>
                </div>
            )}
        </div>
    );
}
